import {FormControl, FormField} from 'platform/components';
import {GridItem, Show} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {head, isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetManufacturersQuery} from '@dms/api/core';
import {useGetAllApiEnumsQuery, useGetMakeModelWithMakeQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {getActiveManufacturerOptions} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {DiscountFormType} from '../types/DiscountFormType';
import {getMakeOptions} from '../utils/getMakeOptions';
import {getModelFamilyOptions} from '../utils/getModelFamilyOptions';
import {getModelOptions} from '../utils/getModelOptions';
import {getTypeOptions} from '../utils/getTypeOptions';

const VEHICLE_TYPE_PROPERTY = 'vehicle_type';

interface VehicleFieldsProps extends TestIdProps {
  control: FormControl<DiscountFormType>;
  formApi: UseFormReturn<DiscountFormType>;
  showSparePartManufacturers?: boolean;
}

export function VehicleFields(props: VehicleFieldsProps) {
  const vehicleType = useWatch({control: props.formApi.control, name: 'vehicleType'});
  const vehicleMake = useWatch({control: props.formApi.control, name: 'vehicleMake'});
  const vehicleModelFamilies = useWatch({
    control: props.formApi.control,
    name: 'vehicleModelFamilies',
  });
  const vehicleModels = useWatch({control: props.formApi.control, name: 'vehicleModels'});

  const hasVehicleModelFamilies = isNotNilOrEmpty(vehicleModelFamilies);
  const hasVehicleModels = isNotNilOrEmpty(vehicleModels);
  // Keep both fields editable when a legacy record already contains both selections.
  const isModelFamilySelectDisabled =
    isNil(vehicleMake) || (hasVehicleModels && !hasVehicleModelFamilies);
  const isModelSelectDisabled =
    isNil(vehicleMake) || (hasVehicleModelFamilies && !hasVehicleModels);

  const {data: manufacturers, isLoading: isManufacturersLoading} = useGetManufacturersQuery(
    undefined,
    {skip: props.showSparePartManufacturers !== true}
  );
  const {data: makes, isLoading: isMakesLoading} = useGetMakeModelWithMakeQuery(
    {
      vehicleType: vehicleType ?? '',
      lang: [i18n.language],
    },
    {
      skip: isNil(vehicleType),
    }
  );
  const {data: models, isLoading: isModelsLoading} = useGetMakeModelWithMakeQuery(
    {
      vehicleType: vehicleType ?? '',
      make: vehicleMake ?? undefined,
      lang: [i18n.language],
    },
    {
      skip: isNil(vehicleType) || isNil(vehicleMake),
      selectFromResult: (result) => ({
        ...result,
        data: head(result?.data ?? [])?.models,
      }),
    }
  );
  const {data: vehicleTypes, isLoading: isVehicleTypesLoading} = useGetAllApiEnumsQuery(
    {lang: [i18n.language]},
    {
      selectFromResult: (result) => ({
        ...result,
        data: result.data?.find((item) => item.property_name === VEHICLE_TYPE_PROPERTY)?.keys,
      }),
    }
  );

  const handleMakeChange = () => {
    props.formApi.setValue('vehicleModels', []);
    props.formApi.setValue('vehicleModelFamilies', []);
  };

  const handleTypeChange = () => {
    props.formApi.setValue('vehicleMake', null);
    handleMakeChange();
  };

  return (
    <>
      <FormField
        control={props.control}
        name="vehicleType"
        type="choice"
        label={i18n.t('entity.vehicle.labels.type')}
        options={getTypeOptions(vehicleTypes ?? [])}
        isLoading={isVehicleTypesLoading}
        onChange={handleTypeChange}
        data-testid={suffixTestId('vehicleType', props)}
      />
      <FormField
        control={props.control}
        name="vehicleMake"
        type="choice"
        label={i18n.t('entity.vehicle.labels.make')}
        options={getMakeOptions(makes ?? [])}
        isLoading={isMakesLoading}
        onChange={handleMakeChange}
        isDisabled={isNil(vehicleType)}
        data-testid={suffixTestId('vehicleMake', props)}
      />
      <GridItem span={[1, 2]}>
        <FormField
          control={props.control}
          name="vehicleModelFamilies"
          type="multiChoice"
          label={i18n.t('entity.vehicle.labels.modelFamily')}
          options={getModelFamilyOptions(models ?? [])}
          isLoading={isModelsLoading}
          isDisabled={isModelFamilySelectDisabled}
          data-testid={suffixTestId('vehicleModelFamilies', props)}
          isNotClearable
          closeMenuOnSelect={false}
        />
      </GridItem>
      <GridItem span={[1, 2]}>
        <FormField
          control={props.control}
          name="vehicleModels"
          type="multiChoice"
          label={i18n.t('entity.vehicle.labels.model')}
          options={getModelOptions(models ?? [])}
          isLoading={isModelsLoading}
          isDisabled={isModelSelectDisabled}
          data-testid={suffixTestId('vehicleModels', props)}
          isNotClearable
          closeMenuOnSelect={false}
        />
      </GridItem>
      <Show when={props.showSparePartManufacturers}>
        <FormField
          control={props.control}
          name="sparePartManufacturers"
          type="multiChoice"
          label={i18n.t('entity.customerContract.labels.sparePartManufacturer')}
          options={getActiveManufacturerOptions(manufacturers)}
          isLoading={isManufacturersLoading}
          data-testid={suffixTestId('sparePartManufacturers', props)}
          closeMenuOnSelect={false}
        />
      </Show>
    </>
  );
}
